import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Animated, ScrollView } from 'react-native'

// Components
import DialogueAutolink from 'APP/Components/DialogueAutolink'
import DialogueMarkdown from 'APP/Components/DialogueMarkdown'
// Styles
import styled, { useTheme } from 'styled-components/native'
import { INTAKE_MAX_WIDTH } from 'APP/Components/Intake/constants'

import { QuestionDescription } from 'APP/Converse/Intake/QuestionDescription'
import {
  DescriptionStyle,
  MemberAppFullscreen,
  RichContentUnion,
} from 'APP/Services/Mattermost/types'
import { isPostTypePromptFile } from '../helper'
import { isMobile } from 'APP/Helpers/checkPlatform'
import { StructuredDescription } from './structuredDescription'

const QuestionContainer = styled(Animated.View)`
  flex-shrink: 3;
  flex-grow: 0;
  width: 100%;
  max-width: ${INTAKE_MAX_WIDTH};
  align-self: center;
`

const QuestionScrollView = styled.ScrollView`
  padding-horizontal: ${({ theme }) => theme.metrics.baseMargin};
`

const SubQuestionScrollView = styled(QuestionScrollView)`
  padding-horizontal: ${({ theme }) => theme.metrics.baseMargin};
`

const QuestionAutolink = styled(DialogueAutolink)`
  font-family: ${({ theme }) => theme.fonts.fontTypes.header};
  font-size: ${({ theme }) => theme.fonts.fontSize.h2};
  line-height: ${({ theme }) => theme.fonts.lineHeight.h2};
  color: ${({ theme }) => theme.colors.text};
  align-self: flex-start;
  margin-top: ${({ theme }) => theme.metrics.baseMargin * 1.5};
  padding-bottom: ${({ theme }) => theme.metrics.baseMargin};
`

const SubQuestionAutolink = styled(DialogueAutolink)`
  line-height: ${({ theme }) => theme.fonts.lineHeight.h3};
  align-self: flex-start;
  margin-top: ${({ theme }) => theme.metrics.baseMargin * 1.5};
`

interface PostProps {
  description?: string
  member_app_fullscreen?: MemberAppFullscreen
  structured_description?: RichContentUnion[]
  [key: string]: any
}

interface Post {
  id: string
  message: string
  props?: PostProps
}

interface IntakeQuestionProps {
  post: Post
  onQuestionShow?: () => void
  currentStepIndex?: number
  multiPromptUtteranceKey?: string | null
  scrollEnabled?: boolean
  animated?: boolean
}

const IntakeQuestion: React.FC<IntakeQuestionProps> = ({
  post: postProp,
  onQuestionShow,
  currentStepIndex,
  multiPromptUtteranceKey = null,
  scrollEnabled,
  animated = true,
}) => {
  const theme = useTheme()
  const [post, setPost] = useState<Post | undefined>()
  const scrollRef = useRef<ScrollView>(null)
  // for prompt_file, we want to show a smaller image height
  const imageHeight = isPostTypePromptFile(postProp) && isMobile() ? 200 : 340

  const markdownStyles = {
    p: {
      fontFamily: theme.fonts.fontTypes.light,
      fontSize: theme.fonts.fontSize.regular,
      lineHeight: theme.fonts.lineHeight.regular,
      minWidth: 220,
      color: theme.colors.dim,
    },
    li: {
      fontFamily: theme.fonts.fontTypes.light,
      fontSize: theme.fonts.fontSize.regular,
      lineHeight: theme.fonts.lineHeight.regular,
      color: theme.colors.dim,
    },
    imageWrapper: {
      height: imageHeight,
    },
  }

  const cardMarkdownStyles = {
    p: {
      fontFamily: theme.fonts.fontTypes.light,
      fontSize: theme.fonts.fontSize.regular,
      lineHeight: 21,
      minWidth: 220,
    },
    imageWrapper: {
      height: imageHeight,
    },
  }

  const questionContainerAnimatedStyle = {}

  useEffect(() => {
    if (post && postProp && post.id === postProp.id && post !== postProp) {
      setPost(postProp)
    }
  }, [postProp, post])

  useEffect(() => {
    const showQuestionOnFinish = () => {
      if (scrollRef.current) {
        scrollRef.current.scrollTo({ y: 0, animated: false })
      }
      setPost(postProp)

      if (postProp && onQuestionShow) {
        onQuestionShow()
      }
      if (!postProp && multiPromptUtteranceKey) {
        onQuestionShow?.()
      }
    }

    showQuestionOnFinish()
  }, [postProp?.id, animated, multiPromptUtteranceKey, onQuestionShow])

  const { message, props: { description, member_app_fullscreen, structured_description } = {} } =
    post || {}

  const renderQuestionHeader = useCallback(() => {
    if (multiPromptUtteranceKey) {
      return <SubQuestionAutolink variant="bodyNormalBold" text={message} tabIndex={0} />
    }
    return <QuestionAutolink text={message} />
  }, [message, multiPromptUtteranceKey])

  const QuestionScrollViewCurrent = useMemo(() => {
    return multiPromptUtteranceKey ? SubQuestionScrollView : QuestionScrollView
  }, [multiPromptUtteranceKey])

  return (
    <QuestionContainer style={questionContainerAnimatedStyle}>
      {message && (
        <QuestionScrollViewCurrent
          ref={scrollRef}
          alwaysBounceVertical={false}
          scrollEnabled={scrollEnabled}
        >
          {renderQuestionHeader()}
          {/* Default to structured description if both description and structured_description are present.
          Description will be soon deprecated [DIA-88232] [DIA-88233] [DIA-87624] */}
          {description && !structured_description && (
            <QuestionDescription member_app_fullscreen={member_app_fullscreen}>
              <DialogueMarkdown
                styles={
                  member_app_fullscreen?.description_style === DescriptionStyle.card
                    ? cardMarkdownStyles
                    : markdownStyles
                }
                markdown={description}
                containerStyles={{}}
              />
            </QuestionDescription>
          )}

          {structured_description && (
            <StructuredDescription
              postId={post?.id}
              structuredDescription={structured_description}
              currentStepIndex={currentStepIndex}
              imageHeight={imageHeight}
            />
          )}
        </QuestionScrollViewCurrent>
      )}
    </QuestionContainer>
  )
}

export default IntakeQuestion
